import { Box, Link, Typography } from '@mui/material'
import { MessageIdentifier } from '~/entities/case'
import { useCallback, useState } from 'react'
import PhoneIcon from '@mui/icons-material/Phone'
import { useDrag } from 'react-dnd'
import MessageInfo from '../Chat/MessageInfo'
import MessageActions from '../Chat/MessageActions'
import { Loader } from '~/shared/ui'
import { messageDateInfo } from '../Chat/utils'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'

type PhoneCommunication = Extract<
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object'],
  { kind: 'phone' }
>

const PhoneCallMessage = (props: {
  phoneCall: Pick<
    PhoneCommunication,
    | 'kind'
    | 'id'
    | 'contact_phone_number'
    | 'direction'
    | 'disposition'
    | 'called_at'
    | 'created_by'
    | 'handled_by'
    | 'recording_url'
    | 'notes'
  > & {
    case_relations: {
      case: Pick<PhoneCommunication['case_relations'][number]['case'], 'id'> & {
        category: Pick<
          PhoneCommunication['case_relations'][number]['case']['category'],
          'id' | 'title'
        >
        relations: (Pick<
          PhoneCommunication['case_relations'][number]['case']['relations'][number],
          'object_id'
        > & {
          content_type: Pick<
            PhoneCommunication['case_relations'][number]['case']['relations'][number]['content_type'],
            'model'
          >
        })[]
      }
    }[]
  }
  person: Pick<
    BffCrmPersonChatRetrieveResponse['person'],
    'id' | 'full_name_used' | 'phone_number' | 'user'
  >
  highlightChatMessageContent?: boolean
  isLastMessage: boolean
  threadUid: string | null
}) => {
  const { phoneCall, person } = props

  const [isLoading, setIsLoading] = useState<boolean>(false)

  let callerOrCalleeNameOrNumber: string | undefined = ''
  /* If we have contact phone number then the call is from talkdesk and was manually linked 
     so checking against person phone number to determine whether to show phone number or patient name.
     Number is to be shown incases such as provider made a call regarding a patient which cannot 
     automatically be linked.
     If we do not have contact phone number then the call was created manually so show patient name.
  */
  if (
    (phoneCall.contact_phone_number &&
      person.phone_number &&
      phoneCall.contact_phone_number?.slice(-10) == person.phone_number?.slice(-10)) ||
    !phoneCall.contact_phone_number
  ) {
    callerOrCalleeNameOrNumber = person.full_name_used
  } else callerOrCalleeNameOrNumber = phoneCall.contact_phone_number

  // Use handled_by (the actual provider who handled the call) if available,
  // falling back to created_by for manually created calls
  const handlerName = phoneCall.handled_by?.full_name_used ?? phoneCall.created_by.full_name_used

  let caller: string | undefined = ''
  let callee: string | undefined = ''
  if (phoneCall.direction === 'INBOUND') {
    caller = callerOrCalleeNameOrNumber
    callee = handlerName
  } else {
    caller = handlerName
    callee = callerOrCalleeNameOrNumber
  }
  const dispositionName = phoneCall.disposition?.title ?? ''

  const { messageDate, preciseDate } = messageDateInfo(phoneCall.called_at)

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'messageIdentifier',
    item: (): MessageIdentifier | { type: 'phonecall'; id: string } => {
      const messageIdentifier: MessageIdentifier | { type: 'phonecall'; id: string } = {
        type: 'phonecall',
        id: phoneCall.id,
      }

      return messageIdentifier
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  }))
  // The connector returns an element, which React 19's ref callbacks may not;
  // memoised so the wrapper keeps the connector's stable identity and the ref
  // is not detached and reattached on every render.
  const dragRef = useCallback((el: HTMLDivElement | null) => void drag(el), [drag])

  const [mouseOnChatText, setMouseOnChatText] = useState<boolean>(false)
  const [mouseDownOnChatBox, setMouseDownOnChatBox] = useState<boolean>(false)
  const draggable = mouseDownOnChatBox && !mouseOnChatText

  // Avoid the hover menu from overlapping with the DOM snapshot
  // during drag-and-drop
  const showMessageActions = !(isDragging || mouseDownOnChatBox)

  // A finished drag releases the mouse-down that started it.
  const [wasDragging, setWasDragging] = useState(isDragging)
  if (isDragging !== wasDragging) {
    setWasDragging(isDragging)
    if (!isDragging) setMouseDownOnChatBox(false)
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        title={messageDate}
        data-id={`phonecall-${phoneCall.id}`}
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          position: 'relative',
          '&:hover .message-actions-hover-control': {
            opacity: '1 !important',
          },
          borderRadius: 2,
          ...(props.highlightChatMessageContent && {
            backgroundColor: 'background.highlight',
          }),
        }}
      >
        {/* Left: avatar */}
        <Box>
          <PhoneIcon sx={{ marginLeft: 1 }} />
        </Box>
        {/* Right: message contents */}
        <Box sx={{ flexGrow: 1 }}>
          {isLoading && <Loader />}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: 0.5 }}>
            <Typography title={preciseDate} sx={{ fontSize: '1.2rem', marginLeft: 3 }}>
              {messageDate}
            </Typography>
          </Box>
          <Box
            sx={{
              display: showMessageActions ? 'initial' : 'none',
            }}
          >
            <MessageActions
              isLastMessage={props.isLastMessage}
              message={phoneCall}
              person={person}
              setIsLoading={setIsLoading}
              threadUid={props.threadUid}
              // (Note that you can still drag and drop phone calls into cases.)
              canCase={false}
            />
          </Box>
          <Box
            tabIndex={0}
            ref={dragRef}
            draggable={draggable}
            onMouseDown={() => setMouseDownOnChatBox(!mouseOnChatText)}
            onMouseUp={() => setMouseDownOnChatBox(false)}
            sx={{
              position: 'relative',
              marginLeft: 3,
              justifyContent: 'flex-start',
              display: 'flex',
              flexWrap: 'wrap',
              opacity: isDragging ? 0.5 : 1,
              justifyItems: 'center',
            }}
          >
            <Box
              sx={{
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 16,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                backgroundColor: 'primary.100',
                color: 'text.primary',
                ['& a']: {
                  color: 'primary.main',
                },
                position: 'relative',
                padding: 1.25,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '1.6rem',
                cursor: 'move',
              }}
            >
              <Typography
                style={{ cursor: 'text' }}
                onMouseOver={() => setMouseOnChatText(true)}
                onMouseOut={() => setMouseOnChatText(false)}
                onFocus={() => setMouseOnChatText(true)}
                onBlur={() => setMouseOnChatText(false)}
              >
                {caller} called {callee}
              </Typography>
              <Typography variant="body2">{dispositionName}</Typography>
              {phoneCall.recording_url ? (
                <Link href={phoneCall.recording_url} target="_blank" rel="noopener noreferrer">
                  Recording
                </Link>
              ) : null}
              {phoneCall.notes ? (
                <Typography style={{ paddingTop: '1rem' }}>Notes: {phoneCall.notes}</Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ ml: 6, mt: 1 }}>
        <MessageInfo message={phoneCall} threadUid={props.threadUid} canUncase={true} />
      </Box>
    </Box>
  )
}

export default PhoneCallMessage
